/**
 * EngineDetailAiDiscovery Component
 *
 * Engine-specific detail view for AI Discovery Engine.
 * Displays overall score breakdown, per-LLM summaries, share of voice,
 * intent coverage map, competitor AI presence, and cross-channel consistency.
 */

import { useState } from 'react';
import { Subheading, Text } from '@a7y/tailwind-catalyst';
import { Badge } from '@a7y/tailwind-catalyst';
import type { AiDiscoveryEngineSpecificAnalysis, UnifiedDetailedAnalysis } from '@a7y/types';
import { websiteFetchFailureSentence, websiteReadSummary } from '@a7y/types';
import { CollapsibleSection } from './CollapsibleSection';
import { ScoreGauge } from './ScoreGauge';
import { CircularGauge } from './CircularGauge';
import { HorizontalBar } from './HorizontalBar';
import { StatCard } from './StatCard';
import { DevCardBadge } from './DevCardBadge';

export interface EngineDetailAiDiscoveryProps {
  /** AI Discovery specific analysis data */
  analysis: AiDiscoveryEngineSpecificAnalysis;
  /** Detailed analysis with score_breakdown */
  detail: UnifiedDetailedAnalysis;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TH_CLASS =
  'px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';

/** Map LLM identifiers to user-friendly labels */
const LLM_DISPLAY_NAMES: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
  claude: 'Claude',
  google_ai_overview: 'Google AI Overview',
};

/** Map LLM identifiers to badge colors */
const LLM_BADGE_COLOR: Record<string, 'green' | 'blue' | 'purple' | 'amber' | 'zinc'> = {
  chatgpt: 'green',
  gemini: 'blue',
  perplexity: 'purple',
  claude: 'amber',
  google_ai_overview: 'zinc',
};

const SENTIMENT_COLOR: Record<string, 'green' | 'amber' | 'red' | 'zinc'> = {
  positive: 'green',
  neutral: 'amber',
  negative: 'red',
  mixed: 'zinc',
};

const SEVERITY_COLOR: Record<string, 'red' | 'amber' | 'yellow'> = {
  high: 'red',
  medium: 'amber',
  low: 'yellow',
};

// ---------------------------------------------------------------------------
// Score Dimension helpers
// ---------------------------------------------------------------------------

interface ScoreDimension {
  key: string;
  label: string;
  score: number;
  color: string;
}

interface LlmMetricTileProps {
  label: string;
  value: string;
  caption?: string;
  className?: string;
}

function buildScoreDimensions(
  breakdown: AiDiscoveryEngineSpecificAnalysis['score_breakdown']
): ScoreDimension[] {
  return [
    {
      key: 'presence',
      label: 'Presence',
      score: breakdown.presence_score,
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      key: 'position',
      label: 'Position',
      score: breakdown.position_score,
      color: 'text-indigo-600 dark:text-indigo-400',
    },
    {
      key: 'sentiment',
      label: 'Sentiment',
      score: breakdown.sentiment_score,
      color: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      key: 'intent_coverage',
      label: 'Intent Coverage',
      score: breakdown.intent_coverage_score,
      color: 'text-violet-600 dark:text-violet-400',
    },
    {
      key: 'competitive_standing',
      label: 'Competitive Standing',
      score: breakdown.competitive_standing_score,
      color: 'text-amber-600 dark:text-amber-400',
    },
  ];
}

function getLlmDisplayName(llm: string): string {
  return LLM_DISPLAY_NAMES[llm.toLowerCase()] ?? llm;
}

function getLlmBadgeColor(llm: string): 'green' | 'blue' | 'purple' | 'amber' | 'zinc' {
  return LLM_BADGE_COLOR[llm.toLowerCase()] ?? 'zinc';
}

function getSentimentBadgeColor(sentiment: string): 'green' | 'amber' | 'red' | 'zinc' {
  return SENTIMENT_COLOR[sentiment.toLowerCase()] ?? 'zinc';
}

function formatPosition(pos: number | null): string {
  if (pos === null || pos === undefined) return 'N/A';
  return `#${pos.toFixed(1)}`;
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/** Format a value already expressed as 0-100 integer percentage. */
function formatPercent100(value: number): string {
  return `${Math.round(value)}%`;
}

/**
 * Narrowing helper for V1 audit fields that the plausibility gate may have
 * rewritten to a `DataQualityFlag` sentinel (A7Y-1083).
 *
 * `@a7y/ui` is a shared package and intentionally does NOT depend on the
 * `DataQualityFlagBadge` component (which lives in `a7y-webapp`). Consumers of
 * `EngineDetailAiDiscovery` are expected to render the quarantine badge higher
 * up the tree (e.g. via `DataQualityFlagModulePlaceholder` for module-scope
 * suppression). At the leaf level here we coerce sentinels to `null` so the
 * arithmetic / comparisons below remain well-typed.
 */
function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

/** Like {@link formatPercent100} but tolerates a `DataQualityFlag` sentinel. */
function formatPercent100OrPlaceholder(value: unknown): string {
  return typeof value === 'number' ? formatPercent100(value) : '—';
}

/** Convert snake_case or lower_case_underscore to Title Case. */
function toTitleCase(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function LlmMetricTile({ label, value, caption, className = '' }: LlmMetricTileProps) {
  return (
    <div
      className={`rounded-lg border border-gray-200/80 bg-gray-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/40 ${className}`}
    >
      <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className="mt-2 block text-xl font-bold font-accent text-zinc-900 dark:text-white">
        {value}
      </span>
      {caption && (
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{caption}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EngineDetailAiDiscovery({
  analysis,
  detail,
  printMode,
  className = '',
}: EngineDetailAiDiscoveryProps) {
  const [scoreOpen, setScoreOpen] = useState(true);
  const [llmOpen, setLlmOpen] = useState(true);
  const [googleDetailOpen, setGoogleDetailOpen] = useState(true);
  const [sovOpen, setSovOpen] = useState(true);
  const [intentMapOpen, setIntentMapOpen] = useState(true);
  const [competitorOpen, setCompetitorOpen] = useState(false);
  const [consistencyOpen, setConsistencyOpen] = useState(true);

  const {
    score_breakdown,
    per_llm_summaries,
    share_of_voice,
    intent_coverage_map,
    per_competitor_analysis,
    consistency_report,
    google_source_breakdown,
  } = analysis;

  const dimensions = buildScoreDimensions(score_breakdown);
  const scoredConsistencyReport =
    consistency_report &&
    consistency_report.website_available !== false &&
    typeof consistency_report.overall_consistency_score === 'number' &&
    typeof consistency_report.clarity_score === 'number' &&
    typeof consistency_report.credibility_score === 'number' &&
    consistency_report.overall_consistency_score >= 0 &&
    consistency_report.clarity_score >= 0 &&
    consistency_report.credibility_score >= 0
      ? {
          ...consistency_report,
          overall_consistency_score: consistency_report.overall_consistency_score,
          clarity_score: consistency_report.clarity_score,
          credibility_score: consistency_report.credibility_score,
        }
      : null;

  // Suppress unused-variable lint for `detail` — kept in signature for API consistency
  void detail;

  return (
    <DevCardBadge name="EngineDetailAiDiscovery">
      <div className={`space-y-6 ${className}`}>
        {/* ---------------------------------------------------------------- */}
        {/* Overall AI Discovery Score                                       */}
        {/* ---------------------------------------------------------------- */}
        <CollapsibleSection
          title="AI Discovery Score"
          isExpanded={scoreOpen}
          onToggle={() => setScoreOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Overall gauge */}
            <div className="flex flex-col items-center gap-1">
              <ScoreGauge score={score_breakdown.overall_score} size="large" />
              <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">Overall Score</Text>
            </div>

            {/* 5-dimension breakdown */}
            <div className="flex-1 w-full space-y-3">
              {dimensions.map((dim) => (
                <div key={dim.key} className="flex items-center gap-3">
                  <span
                    className={`text-xs font-medium w-36 shrink-0 uppercase tracking-wide ${dim.color}`}
                  >
                    {dim.label}
                  </span>
                  <div className="flex-1">
                    <HorizontalBar score={dim.score} label="" showValue />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* ---------------------------------------------------------------- */}
        {/* Per-LLM Breakdown                                                */}
        {/* ---------------------------------------------------------------- */}
        {per_llm_summaries.length > 0 && (
          <CollapsibleSection
            title={`Per-LLM Breakdown (${per_llm_summaries.length})`}
            isExpanded={llmOpen}
            onToggle={() => setLlmOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {per_llm_summaries.map((llm) => {
                const hasCoverage = typeof llm.covered_prompts === 'number';
                const coverageRate = llm.coverage_rate ?? 0;
                const mentionDenominator = hasCoverage
                  ? (llm.covered_prompts as number)
                  : llm.total_prompts;
                const mentionRate = hasCoverage
                  ? (llm.mention_rate_of_covered ?? 0)
                  : llm.total_prompts > 0
                    ? llm.prompts_with_mention / llm.total_prompts
                    : 0;
                const notShown = hasCoverage && coverageRate === 0;
                const notShownLabel =
                  llm.llm.toLowerCase() === 'google_ai_overview'
                    ? 'AI Overview not shown'
                    : 'Not Shown';
                const coverageValue = hasCoverage
                  ? `${llm.covered_prompts}/${llm.total_prompts}`
                  : null;
                const coverageCaption = hasCoverage
                  ? `${formatPercent(coverageRate)} of prompts`
                  : null;
                const mentionValue = `${llm.prompts_with_mention}/${mentionDenominator}`;
                const mentionCaption = hasCoverage
                  ? `${formatPercent(mentionRate)} of covered prompts`
                  : llm.total_prompts > 0
                    ? `${formatPercent(mentionRate)} of prompts`
                    : 'No prompts analyzed';
                return (
                  <div
                    key={llm.llm}
                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm shadow-gray-200/60 dark:border-zinc-700 dark:bg-zinc-900/80 dark:shadow-none"
                  >
                    <div className="space-y-4">
                      {/* LLM Header */}
                      <div className="flex items-start justify-between gap-3">
                        <Badge color={getLlmBadgeColor(llm.llm)}>
                          {getLlmDisplayName(llm.llm)}
                        </Badge>
                        {notShown ? (
                          <Badge color="zinc">{notShownLabel}</Badge>
                        ) : llm.overall_presence_rate < 0.25 ? (
                          <Badge color="zinc">Low Presence</Badge>
                        ) : (
                          <Badge color={getSentimentBadgeColor(llm.dominant_sentiment)}>
                            {llm.dominant_sentiment}
                          </Badge>
                        )}
                      </div>

                      {/* Presence Rate */}
                      <div className="rounded-lg border border-gray-200/80 bg-gray-50/70 p-3 dark:border-zinc-700 dark:bg-zinc-800/40">
                        <div className="mb-2 flex items-end justify-between gap-3">
                          <div>
                            <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                              Presence
                            </span>
                            <span className="mt-1 block text-2xl font-bold font-accent text-zinc-900 dark:text-white">
                              {formatPercent(llm.overall_presence_rate)}
                            </span>
                          </div>
                          <span className="max-w-28 text-right text-xs text-gray-500 dark:text-gray-400">
                            {notShown ? notShownLabel : 'Prompt-level visibility'}
                          </span>
                        </div>
                        <HorizontalBar
                          score={Math.round(llm.overall_presence_rate * 100)}
                          label=""
                          showValue={false}
                        />
                      </div>

                      {/* Key metrics */}
                      <div className="grid grid-cols-1 gap-2">
                        <div
                          className={`grid gap-2 ${hasCoverage ? 'sm:grid-cols-2' : 'grid-cols-1'}`}
                        >
                          <LlmMetricTile
                            label="Avg Position"
                            value={formatPosition(llm.avg_position)}
                            caption="Average ranking when mentioned"
                          />
                          {coverageValue && coverageCaption && (
                            <LlmMetricTile
                              label="Coverage"
                              value={coverageValue}
                              caption={coverageCaption}
                            />
                          )}
                        </div>
                        <LlmMetricTile
                          label="Mentions"
                          value={mentionValue}
                          caption={mentionCaption}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Google Search Detail                                             */}
        {/* ---------------------------------------------------------------- */}
        {google_source_breakdown && (
          <CollapsibleSection
            title="Google Search Detail"
            isExpanded={googleDetailOpen}
            onToggle={() => setGoogleDetailOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            {(() => {
              const {
                ai_overview_served,
                ai_overview_cited,
                organic_analyzed,
                organic_cited,
                avg_organic_position,
              } = google_source_breakdown;
              const totalQueries = ai_overview_served + organic_analyzed;
              return (
                <div className="space-y-2">
                  <Text className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-zinc-900 dark:text-white">AI Overview:</span>{' '}
                    served on{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {ai_overview_served}
                    </span>{' '}
                    of{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {totalQueries}
                    </span>{' '}
                    queries, cited in{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {ai_overview_cited}
                    </span>{' '}
                    of{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {ai_overview_served}
                    </span>
                  </Text>
                  <Text className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-zinc-900 dark:text-white">
                      Organic Results:
                    </span>{' '}
                    target in top-10 on{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {organic_cited}
                    </span>{' '}
                    of{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {organic_analyzed}
                    </span>{' '}
                    queries, avg position{' '}
                    <span className="font-bold font-accent text-zinc-900 dark:text-white">
                      {formatPosition(avg_organic_position)}
                    </span>
                  </Text>
                </div>
              );
            })()}
          </CollapsibleSection>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Share of Voice                                                   */}
        {/* ---------------------------------------------------------------- */}
        <CollapsibleSection
          title="Share of Voice"
          isExpanded={sovOpen}
          onToggle={() => setSovOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <StatCard
              label="Your SoV"
              value={formatPercent100OrPlaceholder(share_of_voice.target_sov)}
            />
            <StatCard label="Total Responses" value={share_of_voice.total_responses} />
            <StatCard label="Competitors Mentioned" value={share_of_voice.total_apps_mentioned} />
          </div>

          {/* Stacked bar visualization */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Subheading level={5}>Distribution</Subheading>
            </div>
            <div className="w-full h-8 bg-gray-200 dark:bg-zinc-700 rounded-full overflow-hidden flex">
              {/* Target app slice */}
              {(() => {
                const targetSov = asNumberOrNull(share_of_voice.target_sov);
                if (targetSov === null) return null;
                return (
                  <div
                    className="bg-indigo-500 transition-all duration-500 flex items-center justify-center"
                    style={{ width: `${Math.max(targetSov, 2)}%` }}
                    title={`Your app: ${formatPercent100(targetSov)}`}
                  >
                    {targetSov >= 8 && (
                      <span className="text-xs font-medium text-white truncate px-1">You</span>
                    )}
                  </div>
                );
              })()}
              {/* Competitor slices */}
              {share_of_voice.competitor_sov.map((comp, idx) => {
                const colors = [
                  'bg-violet-500',
                  'bg-purple-500',
                  'bg-fuchsia-500',
                  'bg-pink-500',
                  'bg-rose-500',
                  'bg-red-400',
                  'bg-orange-400',
                  'bg-amber-400',
                ];
                const bgColor = colors[idx % colors.length];
                const sovPercentage = asNumberOrNull(comp.sov_percentage);
                if (sovPercentage === null) return null;
                return (
                  <div
                    key={comp.competitor_id}
                    className={`${bgColor} transition-all duration-500 flex items-center justify-center`}
                    style={{ width: `${Math.max(sovPercentage, 1)}%` }}
                    title={`${comp.competitor_name}: ${formatPercent100(sovPercentage)}`}
                  >
                    {sovPercentage >= 8 && (
                      <span className="text-xs font-medium text-white truncate px-1">
                        {comp.competitor_name.split(' ')[0]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Competitor SoV table */}
          {share_of_voice.competitor_sov.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                <thead className="bg-gray-50 dark:bg-zinc-800">
                  <tr>
                    <th className={`${TH_CLASS} text-left`}>Competitor</th>
                    <th className={`${TH_CLASS} text-right w-24`}>SoV</th>
                    <th className={`${TH_CLASS} text-right w-24`}>Avg Position</th>
                    <th className={`${TH_CLASS} text-center w-24`}>Sentiment</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
                  {share_of_voice.competitor_sov.map((comp) => (
                    <tr
                      key={comp.competitor_id}
                      className="hover:bg-gray-50 dark:hover:bg-zinc-800"
                    >
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">
                        {comp.competitor_name}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-400">
                        {formatPercent100OrPlaceholder(comp.sov_percentage)}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-400">
                        {formatPosition(comp.avg_position)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Badge color={getSentimentBadgeColor(comp.dominant_sentiment)}>
                          {comp.dominant_sentiment}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CollapsibleSection>

        {/* ---------------------------------------------------------------- */}
        {/* Intent Coverage Map                                              */}
        {/* ---------------------------------------------------------------- */}
        {intent_coverage_map.items.length > 0 && (
          <CollapsibleSection
            title={`Intent Coverage Map (${intent_coverage_map.items.length} intents)`}
            isExpanded={intentMapOpen}
            onToggle={() => setIntentMapOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            {/* Headline stat */}
            <div className="flex items-center gap-3 mb-4">
              <CircularGauge
                score={Math.round(intent_coverage_map.overall_coverage_rate)}
                size="sm"
              />
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Overall coverage rate:{' '}
                <span className="font-bold text-zinc-900 dark:text-white">
                  {formatPercent100(intent_coverage_map.overall_coverage_rate)}
                </span>
              </Text>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                <thead className="bg-gray-50 dark:bg-zinc-800">
                  <tr>
                    <th className={`${TH_CLASS} text-left`}>Intent</th>
                    <th className={`${TH_CLASS} text-right w-32`}>Mention Rate</th>
                    <th className={`${TH_CLASS} text-right w-28`}>Avg Position</th>
                    <th className={`${TH_CLASS} text-left`}>LLMs Mentioning</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
                  {intent_coverage_map.items.map((item) => (
                    <tr key={item.intent_id} className="hover:bg-gray-50 dark:hover:bg-zinc-800">
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                        {item.intent_statement}
                      </td>
                      <td className="px-4 py-2 w-32">
                        <HorizontalBar
                          score={Math.round(item.mention_rate * 100)}
                          label=""
                          showValue
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-400">
                        {formatPosition(item.avg_position)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {item.llms_mentioning.map((llm) => (
                            <Badge key={llm} color={getLlmBadgeColor(llm)}>
                              {getLlmDisplayName(llm)}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Competitor AI Presence                                           */}
        {/* ---------------------------------------------------------------- */}
        {per_competitor_analysis.length > 0 && (
          <CollapsibleSection
            title={`Competitor AI Presence (${per_competitor_analysis.length})`}
            isExpanded={competitorOpen}
            onToggle={() => setCompetitorOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                <thead className="bg-gray-50 dark:bg-zinc-800">
                  <tr>
                    <th className={`${TH_CLASS} text-left`}>Competitor</th>
                    <th className={`${TH_CLASS} text-right w-28`}>Presence</th>
                    <th className={`${TH_CLASS} text-right w-28`}>Avg Position</th>
                    <th className={`${TH_CLASS} text-center w-24`}>Sentiment</th>
                    <th className={`${TH_CLASS} text-right w-36`}>They Outperform</th>
                    <th className={`${TH_CLASS} text-right w-36`}>You Outperform</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
                  {per_competitor_analysis.map((comp) => (
                    <tr
                      key={comp.competitor_id}
                      className="hover:bg-gray-50 dark:hover:bg-zinc-800"
                    >
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">
                        {comp.competitor_name}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-400">
                        {formatPercent100(comp.presence_rate)}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-400">
                        {formatPosition(comp.avg_position)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Badge color={getSentimentBadgeColor(comp.dominant_sentiment)}>
                          {comp.dominant_sentiment}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        <span className="text-red-600 dark:text-red-400 font-medium">
                          {comp.prompts_outperforming} prompts
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {comp.prompts_outperformed_by_target} prompts
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Cross-Channel Consistency                                        */}
        {/* ---------------------------------------------------------------- */}
        {consistency_report && (
          <CollapsibleSection
            title="Cross-Channel Consistency"
            isExpanded={consistencyOpen}
            onToggle={() => setConsistencyOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            {scoredConsistencyReport ? (
              <>
                {/* Consistency scores overview */}
                <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
                  <div className="flex flex-col items-center gap-1">
                    <CircularGauge
                      score={scoredConsistencyReport.overall_consistency_score}
                      size="lg"
                      subtitle="Consistency"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 flex-1 w-full">
                    <StatCard label="Clarity" value={scoredConsistencyReport.clarity_score} />
                    <StatCard
                      label="Credibility"
                      value={scoredConsistencyReport.credibility_score}
                    />
                  </div>
                </div>

                {/* Dimension Scores Table */}
                {consistency_report.dimension_scores.length > 0 && (
                  <div className="mb-6">
                    <Subheading level={5} className="mb-3">
                      Dimension Scores
                    </Subheading>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                        <thead className="bg-gray-50 dark:bg-zinc-800">
                          <tr>
                            <th className={`${TH_CLASS} text-left`}>Dimension</th>
                            <th className={`${TH_CLASS} text-right w-20`}>Score</th>
                            <th className={`${TH_CLASS} text-left`}>Store Says</th>
                            <th className={`${TH_CLASS} text-left`}>Website Says</th>
                            <th className={`${TH_CLASS} text-left`}>Analysis</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
                          {consistency_report.dimension_scores.map((dim) => (
                            <tr
                              key={dim.dimension}
                              className="hover:bg-gray-50 dark:hover:bg-zinc-800"
                            >
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                                {toTitleCase(dim.dimension)}
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                <span
                                  className={`font-bold font-accent ${
                                    dim.score >= 70
                                      ? 'text-green-600 dark:text-green-400'
                                      : dim.score >= 50
                                        ? 'text-yellow-600 dark:text-yellow-400'
                                        : 'text-red-600 dark:text-red-400'
                                  }`}
                                >
                                  {dim.score}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 max-w-[200px]">
                                {dim.store_says}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 max-w-[200px]">
                                {dim.website_says}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 max-w-[200px]">
                                {dim.analysis}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Misalignments */}
                {consistency_report.misalignments.length > 0 && (
                  <div>
                    <Subheading level={5} className="mb-3">
                      Misalignments ({consistency_report.misalignments.length})
                    </Subheading>
                    <div className="space-y-3">
                      {consistency_report.misalignments.map((mis, idx) => (
                        <div
                          key={idx}
                          className={`border rounded-lg p-4 ${
                            mis.severity === 'high'
                              ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                              : mis.severity === 'medium'
                                ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                                : 'border-yellow-300 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-900/10'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Badge color={SEVERITY_COLOR[mis.severity]}>
                              {mis.severity.charAt(0).toUpperCase() + mis.severity.slice(1)}
                            </Badge>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {toTitleCase(mis.dimension)}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                            <div className="bg-white/60 dark:bg-zinc-800/40 rounded p-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                Store Says
                              </span>
                              <Text className="text-sm">{mis.store_says}</Text>
                            </div>
                            <div className="bg-white/60 dark:bg-zinc-800/40 rounded p-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                Website Says
                              </span>
                              <Text className="text-sm">{mis.website_says}</Text>
                            </div>
                          </div>
                          <Text className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Fix: </span>
                            {mis.recommendation}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/40">
                <Text className="text-sm font-medium text-gray-900 dark:text-white">
                  Cross-channel consistency was unavailable for this audit.
                </Text>
                {(consistency_report.fetch_failures ?? []).length > 0 ? (
                  // A7Y-5499 — one coded sentence per page we could not read.
                  // A7Y-6001 — led by the one plain line every surface prints.
                  <ul className="mt-1 space-y-1">
                    {websiteReadSummary(consistency_report) ? (
                      <li data-testid="website-read-summary">
                        <Text className="text-sm text-gray-900 dark:text-white">
                          {websiteReadSummary(consistency_report)}
                        </Text>
                      </li>
                    ) : null}
                    {(consistency_report.fetch_failures ?? []).map((failure) => (
                      <li key={failure.url}>
                        <Text className="text-sm text-gray-600 dark:text-gray-400">
                          {websiteFetchFailureSentence(failure)}
                        </Text>
                      </li>
                    ))}
                  </ul>
                ) : consistency_report.website_available === false &&
                  consistency_report.analyzed_urls.length === 0 ? (
                  <Text className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    No website is linked from the store listing. Add it in Project Settings →
                    Website to enable this check.
                  </Text>
                ) : (
                  <Text className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    We could not extract enough meaningful website content to compare against the
                    store listing, so consistency metrics were not scored.
                  </Text>
                )}
              </div>
            )}

            {/* Website URLs (A7Y-800: now multi-source) */}
            {(() => {
              // Prefer `analyzed_urls` from the multi-URL backend; fall back
              // to the legacy single-URL `website_url` shape on older
              // persisted reports so this surface keeps rendering.
              const legacy = consistency_report as unknown as { website_url?: string };
              const urls =
                Array.isArray(consistency_report.analyzed_urls) &&
                consistency_report.analyzed_urls.length > 0
                  ? consistency_report.analyzed_urls
                  : legacy.website_url
                    ? [legacy.website_url]
                    : [];
              if (urls.length === 0) return null;
              const label = scoredConsistencyReport
                ? urls.length > 1
                  ? 'Websites analyzed:'
                  : 'Website analyzed:'
                : urls.length > 1
                  ? 'Websites attempted:'
                  : 'Website attempted:';
              return (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-zinc-700">
                  <Text className="text-xs text-gray-500 dark:text-gray-400">
                    {label}{' '}
                    {urls.map((u, i) => (
                      <span key={u}>
                        {i > 0 && ', '}
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {u}
                        </a>
                      </span>
                    ))}
                  </Text>
                </div>
              );
            })()}
          </CollapsibleSection>
        )}
      </div>
    </DevCardBadge>
  );
}
